/**
 * DIEUKYDIEU.TV - UI & MICRO-INTERACTIONS MODULE
 * Auto-hide header, back-to-top, Material 3 ripple effect, toast notifications, smooth horizontal drag/scroll.
 */

import { state } from './state.js';

let lastScrollY = typeof window !== 'undefined' ? window.scrollY : 0;
let scrollListenersInitialized = false;
let toastTimerId = null;

export function initHeaderAndScrollListeners() {
    if (scrollListenersInitialized || typeof window === 'undefined') return;
    scrollListenersInitialized = true;

    let scrollTicking = false;
    window.addEventListener('scroll', () => {
        if (scrollTicking) return;
        scrollTicking = true;
        window.requestAnimationFrame(() => {
            scrollTicking = false;
            const currentScrollY = window.scrollY;
            const header = document.getElementById('main-header');
            const btt = document.getElementById('back-to-top');

            // Header Auto-hide: Chỉ áp dụng trên Mobile (< 768px), Desktop luôn cố định đỉnh
            if (header) {
                if (window.innerWidth < 768) {
                    if (currentScrollY <= 40) {
                        header.classList.remove('hide-up');
                    } else if (currentScrollY > lastScrollY + 6) {
                        header.classList.add('hide-up');
                    } else if (currentScrollY < lastScrollY - 6) {
                        header.classList.remove('hide-up');
                    }
                } else {
                    header.classList.remove('hide-up');
                }
            }

            lastScrollY = currentScrollY;

            // Back to Top Button: Show when scroll > 300px
            if (btt) {
                if (currentScrollY > 300) {
                    btt.classList.add('show');
                } else {
                    btt.classList.remove('show');
                }
            }

            // Apple News Reading Progress Indicator (Active in Story Detail View)
            const progressBar = document.getElementById('reading-progress-bar');
            if (progressBar) {
                const storyView = document.getElementById('story-detail-view');
                if (storyView && storyView.classList.contains('active')) {
                    progressBar.classList.add('active');
                    const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
                    const progress = totalHeight > 0 ? Math.min(100, Math.max(0, (currentScrollY / totalHeight) * 100)) : 0;
                    progressBar.style.width = `${progress}%`;
                } else {
                    progressBar.classList.remove('active');
                    progressBar.style.width = '0%';
                }
            }
        });
    }, { passive: true });

    // Global Escape Key Handler for Popovers, Modals, Sheets & Drawers
    window.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') return;

        const readerPopover = document.getElementById('apple-reader-popover');
        if (readerPopover && readerPopover.classList.contains('show')) {
            if (typeof window.closeStoryReaderPopover === 'function') window.closeStoryReaderPopover();
            else readerPopover.classList.remove('show');
            return;
        }

        const langMenu = document.getElementById('lang-menu');
        const sidebarLangMenu = document.getElementById('sidebar-lang-menu');
        if ((langMenu && langMenu.classList.contains('show')) || (sidebarLangMenu && sidebarLangMenu.classList.contains('show'))) {
            if (langMenu) langMenu.classList.remove('show');
            if (sidebarLangMenu) sidebarLangMenu.classList.remove('show');
            const sidebarLangBackdrop = document.getElementById('sidebar-lang-backdrop');
            if (sidebarLangBackdrop) sidebarLangBackdrop.classList.remove('show');
            return;
        }

        const shareModal = document.getElementById('share-modal');
        if (shareModal && shareModal.classList.contains('show')) {
            if (typeof window.closeShareModal === 'function') window.closeShareModal();
            else shareModal.classList.remove('show');
            return;
        }

        const livechatModal = document.getElementById('livechat-modal');
        if (livechatModal && livechatModal.classList.contains('show')) {
            if (typeof window.toggleLiveChat === 'function') window.toggleLiveChat();
            return;
        }

        const mobileSearch = document.getElementById('mobile-search-modal');
        if (mobileSearch && mobileSearch.classList.contains('show')) {
            if (typeof window.closeMobileSearch === 'function') window.closeMobileSearch();
            return;
        }

        const desktopSearch = document.getElementById('desktop-search-results');
        if (desktopSearch && desktopSearch.classList.contains('show')) {
            if (typeof window.closeDesktopSearchDropdown === 'function') window.closeDesktopSearchDropdown();
            return;
        }

        const fullPlayer = document.getElementById('full-player');
        if (fullPlayer && fullPlayer.classList.contains('open')) {
            if (typeof window.closeFullPlayer === 'function') window.closeFullPlayer();
            return;
        }

        const mobileNav = document.getElementById('main-nav');
        if (mobileNav && mobileNav.classList.contains('open')) {
            closeMobileSidebar();
        }
    });
}

export function scrollToTop() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
}

export function toggleDesktopSidebar() {
    state.isSidebarCollapsed = !state.isSidebarCollapsed;
    const nav = document.getElementById('main-nav');
    const content = document.querySelector('.content-wrapper');
    if (nav) nav.classList.toggle('collapsed', state.isSidebarCollapsed);
    if (content) content.classList.toggle('expanded', state.isSidebarCollapsed);
}

export function toggleMobileSidebar() {
    const nav = document.getElementById('main-nav');
    const backdrop = document.getElementById('sidebar-backdrop');
    if (!nav) return;
    const isOpen = nav.classList.toggle('open');
    if (backdrop) backdrop.classList.toggle('active', isOpen);
    document.body.style.overflow = isOpen ? 'hidden' : '';
}

export function closeMobileSidebar() {
    const nav = document.getElementById('main-nav');
    const backdrop = document.getElementById('sidebar-backdrop');
    if (nav) nav.classList.remove('open');
    if (backdrop) backdrop.classList.remove('active');
    const sidebarLangMenu = document.getElementById('sidebar-lang-menu');
    if (sidebarLangMenu) sidebarLangMenu.classList.remove('show');
    const sidebarLangBackdrop = document.getElementById('sidebar-lang-backdrop');
    if (sidebarLangBackdrop) sidebarLangBackdrop.classList.remove('show');
    document.body.style.overflow = '';
}

export function showToast(msg) {
    const toast = document.getElementById('toast-notify');
    if (!toast) return;
    if (toastTimerId) {
        clearTimeout(toastTimerId);
    }
    toast.innerText = msg;
    toast.classList.add('show');
    toastTimerId = setTimeout(() => {
        toast.classList.remove('show');
        toastTimerId = null;
    }, 2400);
}

export function initMaterial3Ripple() {
    // Apple Design Architecture: Loại bỏ hoàn toàn việc inject vệt sóng nước M3 Ripple.
    // Toàn bộ tương tác phản hồi xúc giác (Tactile Feedback) đã được xử lý mượt mà 
    // và hiệu năng cao qua Apple Spring Micro-interactions trong CSS (:active).
}

// Hỗ trợ cuộn dọc tự nhiên khi rê chuột vào thanh cuộn ngang & Kéo chuột rê ngang (drag to scroll)
export function enableSmoothHorizontalInteraction(el) {
    if (!el || el.dataset.smoothInteractionBound) return;
    el.dataset.smoothInteractionBound = 'true';

    let isDown = false;
    let startX = 0;
    let scrollLeft = 0;
    let hasMoved = false;

    el.addEventListener('mousedown', (e) => {
        if (e.button !== 0 || e.target.closest('button, a')) return;
        isDown = true;
        hasMoved = false;
        startX = e.pageX - el.offsetLeft;
        scrollLeft = el.scrollLeft;
        el.classList.add('is-dragging');
    });

    window.addEventListener('mouseup', () => {
        if (isDown) {
            isDown = false;
            el.classList.remove('is-dragging');
            setTimeout(() => { hasMoved = false; }, 80);
        }
    });

    el.addEventListener('mousemove', (e) => {
        if (!isDown) return;
        const x = e.pageX - el.offsetLeft;
        const walk = (x - startX) * 1.3;
        if (Math.abs(walk) > 4) {
            hasMoved = true;
        }
        el.scrollLeft = scrollLeft - walk;
    });

    el.addEventListener('click', (e) => {
        if (hasMoved) {
            e.preventDefault();
            e.stopPropagation();
        }
    }, true);

    // Khi rê chuột và lăn bánh xe dọc (wheel), truyền trực tiếp thao tác cuộn dọc lên trang web
    el.addEventListener('wheel', (e) => {
        if (e.shiftKey || Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
            return;
        }
        if (Math.abs(e.deltaY) > 0) {
            window.scrollBy({
                top: e.deltaY,
                behavior: 'auto'
            });
        }
    }, { passive: true });
}
